import { Pagination, Space, Spin, TableColumnsType, Typography } from "antd";
import { debounce, map } from "lodash";
import moment from "moment/moment";
import { ContentWrapper, DataTable } from "~/Components";
import { get } from "~/Services";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNotification } from "@hooks/useNotification";
import { useQuery } from "@hooks/useQuery";
import { getFormattedDate } from "@utils/CommonUtils";

export const Logs = () => {
  const { id } = useParams();
  const { openToast } = useNotification();
  const [data, setData] = useState<any>([]);
  const [total, setTotal] = useState<any>(0);
  const [isLoading, setIsLoading] = useState(false);
  const query = useQuery();
  const [pageStart, setPageStart] = useState<number>(0);
  const [recordsPerPage, setRecordsPerPage] = useState<number>(10);
  const [page, setpage] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const [orderByName, setOrderByName] = useState("actionPerformedOn");
  const [searchString, setSearchString] = useState("");

  const columns: TableColumnsType = [
    {
      title: "Action",
      dataIndex: "action",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.action || "").localeCompare(b.action || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "action"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "action"
            ? "descend"
            : undefined,
    },
    {
      title: "Details",
      dataIndex: "detail",
      render: (value: any, record: any) => {
        if (!value) return <Typography.Text disabled>-</Typography.Text>;

        const decoded = decodeURIComponent(value);

        const formatted = decoded.replace(/\b(\d+)\.(\d+)\b/g, (_, major, minor) => {
          const version = (Number(major) - 1) * 10 + Number(minor);
          return `V${version}`;
        });

        return formatted;
      },
      sorter: (a: any, b: any) => (a?.detail || "").localeCompare(b?.detail || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "detail"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "detail"
            ? "descend"
            : undefined,
    },
    {
      title: "Performed By",
      dataIndex: "actionPerformedBy",
      render: (value: any) => value || <Typography.Text disabled>-</Typography.Text>,
      sorter: (a: any, b: any) => (a.actionPerformedBy || "").localeCompare(b.actionPerformedBy || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "actionPerformedBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "actionPerformedBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Performed At",
      dataIndex: "actionPerformedOn",
      render: (value: any) => getFormattedDate(value) || <Typography.Text disabled>-</Typography.Text>,
      // change here
      sorter: (a: any, b: any) => (a.actionPerformedOn || "").localeCompare(b.actionPerformedOn || ""),
      sortOrder:
        sortOrder === "asc" && orderByName === "actionPerformedOn"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "actionPerformedOn"
            ? "descend"
            : undefined,
    },
  ];

  const getLogs = (sortOrder: any, orderByName: any, searchString: any, startIndex: any) => {
    let url = `v1/auditlog/envelope/get-all-logs?envelopeId=${id}&page=${startIndex}&size=${recordsPerPage}&searchText=${searchString}&order=${sortOrder}&orderBy=${orderByName}`;
    get(url)
      .then((res: any) => {
        if (res && res.response && res.response.data) {
          setData(res.response.data.records);
          setTotal(res.response.data.totalCount);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        // openToast({
        //   content: err,
        //   type: "error",
        // });
        setIsLoading(false);
      });
  };

  const handleTableSort = (column: any, sortOrder: string, page: any) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    getLogs(order, column.dataIndex, searchString, pageStart);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
  }, 1000);

  useEffect(() => {
    getLogs(sortOrder, orderByName, searchString, searchString.length > 0 ? 0 : pageStart);
  }, [searchString, pageStart]);

  return (
    <ContentWrapper>
      <DataTable
        // height={400}
        multiSelect={false}
        tableData={data}
        columns={columns?.map((column: any) => ({
          ...column,
          onHeaderCell: (column: any) => ({
            onClick: () => {
              if (column.sorter) {
                handleTableSort(column, sortOrder, page ? page : 0);
              }
            },
          }),
        }))}
        onSearchChange={handleSearchChange}
        searchPlaceholderText={"Search"}
        showTopBar={false}
        recordsPerPage={recordsPerPage}
        hideExport={true}
        isLoading={isLoading}
        isPagination={true}
        isServerSearchApi={true}
        tabOffset={80}
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          total={total}
          current={page + 1}
          onChange={(page) => {
            const pageSize = 10;
            const startIndex = page - 1;
            setpage(page - 1);
            setPageStart(startIndex);
            getLogs(sortOrder, orderByName, searchString, startIndex);
          }}
          showSizeChanger={false}
        />
      </div>
    </ContentWrapper>
  );
};
